import React, { useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn as Column, GridCellProps } from '@progress/kendo-react-grid';
import { t } from 'i18next';

export interface AuditEntriesModel {
  Id: number;
  ProjectId: number;
  EntityType: string;
  OperationType: string;
  CreationDate: string;
  CreatedBy: number;
  Differences: AuditEntryDifference[];
}    

interface AuditEntryDifference {
  Field: string;
  Operation: string;
  OldValue: string;
  NewValue: string;
}

interface Props {
  onClose: () => void;
  auditEntriesModel: AuditEntriesModel[];
  entityTitleName?: string | null;
}

export const AuditEntryModal: React.FC<Props> = ({ onClose, auditEntriesModel, entityTitleName }) => {
  const [data, setData] = useState(
    Array.isArray(auditEntriesModel)
    ? auditEntriesModel.map(item => ({ ...item, expanded: false }))
    : []
  );

  const handleExpandChange = (event: any) => {
    const { OperationType, Id } = event.dataItem;
    if (OperationType === 'CREATE' || OperationType === 'ASSIGN' || OperationType === 'UNASSIGN') {
      return; // Block expand for CREATE entries
    }
  
    const updated = data.map(item =>
      item.Id === Id ? { ...item, expanded: !item.expanded } : item
    );
    setData(updated);
  };

  const getHeaderCell = (label: string, opacity?: number) => () => (
    <th
      className="k-table-th"
      role="columnheader"
      scope="col"
      style={{
        backgroundColor: `rgba(77, 247, 185, ${opacity ?? 1})`,
        color: '#000',
        fontWeight: opacity === 1 ? 'bold' : 'normal',
        padding: '8px',
        border: '1px solid #ddd'
      }}
    >
      {label}
    </th>
  );

  const OldCells = {
    headerCell: getHeaderCell(t("Audit.oldColumnTitle"), 0.5),
    data: (props: GridCellProps) => (
      <td style={{ verticalAlign: 'top', padding: '8px' }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{props.dataItem.OldValue}</pre>
      </td>
    )
  };
  
  const NewCells = {
    headerCell: getHeaderCell(t("Audit.newColumnTitle"), 0.5),
    data: (props: GridCellProps) => (
      <td style={{ verticalAlign: 'top' }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
          {props.dataItem.NewValue}
        </pre>
      </td>
    )
  };

  const DateCells = {
    headerCell: getHeaderCell(t("Audit.dateColumnTitle"), 1),
    data: (props: GridCellProps) => (
      <td style={{ verticalAlign: 'top' }}>
          {new Date(props.dataItem.CreationDate).toLocaleString()}
      </td>
    )
  };

  const CustomRow = (props: any) => {
    const item = props.dataItem as AuditEntriesModel;
    const noExpand = item.OperationType === 'CREATE' || item.OperationType === 'ASSIGN' || item.OperationType === 'UNASSIGN';
  
    const className = noExpand
      ? `${props.trProps.className || ''} no-expand`
      : props.trProps.className;
  
    return <tr {...props.trProps} className={className}>{props.children}</tr>;
  };

  return (
    <Dialog title={entityTitleName 
                    ? `${t("Audit.historyModalTitle")} - ${entityTitleName}` 
                    : t("Audit.historyModalTitle")} onClose={onClose} width={800}>
      {data.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          {t("Audit.emptyHistoryData")}
        </div>
      ) : (
        <div
          style={{
            border: '1px solid #ddd',   
            height: '500px',
            maxHeight: '70vh',
            overflowY: 'auto'
          }}
        >
          <Grid
            data={data}
            style={{ height: 'auto' }}
            scrollable="none"
            detail={(props: any) => {
              const diffs: AuditEntryDifference[] = props.dataItem.Differences || [];
              return diffs.length === 0 ? (
                <div style={{ padding: '10px 20px' }}>
                  <em>({t("Audit.expandNoDifferences")})</em>
                </div>
              ) : (
                <div style={{ border: '1px solid #ddd'}}>
                  <Grid
                    data={diffs.map(d => ({
                      Field: d.Field,
                      Operation: d.Operation,
                      OldValue: d.OldValue ?? '(empty)',
                      NewValue: d.NewValue ?? '(empty)'
                    }))}
                    scrollable="none"
                    style={{ width: '100%' }}
                  >
                    <Column field="Field" title={t("Audit.fieldColumnTitle")} cells={{headerCell: getHeaderCell(t("Audit.fieldColumnTitle"), 0.5)}} />
                    <Column field="Operation" title={t("Audit.operationColumnTitle")} cells={{ headerCell: getHeaderCell(t("Audit.operationColumnTitle"), 0.5) }} />
                    <Column field="OldValue" title={t("Audit.oldColumnTitle")} cells={OldCells} />
                    <Column field="NewValue" title={t("Audit.newColumnTitle")} cells={NewCells} />
                  </Grid>
                </div>
              );
            }}
            expandField="expanded"
            onExpandChange={handleExpandChange}
            dataItemKey="Id"
            sortable
            rows={{ data: CustomRow }}
          >
            <Column field="CreationDate" title={t("Audit.dateColumnTitle")} cells={DateCells} />
            <Column field="OperationType" title={t("Audit.operationColumnTitle")} cells={{ headerCell: getHeaderCell(t("Audit.operationColumnTitle"), 1) }} />
            <Column field="CreatedBy" title={t("Audit.createdByColumnTitle")} cells={{ headerCell: getHeaderCell(t("Audit.createdByColumnTitle"), 1) }} />
          </Grid>
        </div>
      )}
    </Dialog>
  );  
};